export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type DevicePlatform = 'ios' | 'android' | 'desktop';

const LEADS_DOMAIN = import.meta.env.VITE_LEADS_DOMAIN || '';
const LEADS_HOSTNAME = (() => {
  try { return new URL(LEADS_DOMAIN).hostname; } catch { return ''; }
})();

export const INSTALL_PATH = '/instalar';

export function isLeadsDomain(): boolean {
  return !!LEADS_HOSTNAME && window.location.hostname === LEADS_HOSTNAME;
}

export function getInstallUrl(): string {
  return `${window.location.origin}${INSTALL_PATH}`;
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function detectPlatform(): DevicePlatform {
  const ua = navigator.userAgent;
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || isIPadOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

export function clearInstallPrompt() {
  deferredPrompt = null;
  notify();
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function shouldEnableApp(): boolean {
  const path = window.location.pathname;
  return !isLeadsDomain() && !path.startsWith('/cadastro') && !path.startsWith('/leads');
}

export function initPwa() {
  if (!shouldEnableApp()) return;

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', clearInstallPrompt);

  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register(`/sw.js?v=${__APP_VERSION__}`)
        .catch((error) => console.error('Falha ao registrar o app offline:', error));
    });
  }
}
